import { Price, IPriceData, IPrice } from './Price';
import { Size, ISizeData } from './Size';
import { Shade, IShadeData, ICoreShade } from './Shade';
import { ISkuSkin, SkuSkin } from './SkuSkin';
import { observable, computed } from 'mobx';
import { Media, ImageSizes, IMedia } from './Media';
import { IPerlgem, IFinishOption, IFinish } from '../../../api/ProdcatApiSdk';
import { DEFAULT_IMAGE_ATTRIBUTE } from '../../constants/DefaultImageAttribute';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';
import { Product } from './Product';
import { IKeyValue, IValue } from './BasicEntities';
import { copyPropertiesExcludeNullsAndGetters } from '../../utils/ObjectUtils';
import { ColorFamily } from './ColorFamily';
import { Undertone } from './Undertone';
import { IAsset } from './Asset';
import { InventoryStatus, SHOPPABLE_STATUSES } from '../../constants/InventoryStatuses';
import { toCamelCase } from '../../utils/ToCamelCase';
import { SkuShadeGroup } from './SkuShadeGroup';
import { CrossSellCategory, ICrossSellCategory } from './CrossSellCategory';
import { Tag, ITag } from './Tag';
import { Discount, IDiscountCollection, Discounts } from './Discount';

export interface ISku {
    cross_sell: ICrossSellCategory;
    color_family: IKeyValue[];
    discounts?: IDiscountCollection;
    finish_options: IFinishOption[];
    iln_listing: string;
    iln_version_number: string;
    inventory_status: string;
    is_default_sku: boolean;
    is_discountable: boolean;
    is_engravable: boolean;
    is_giftwrap: boolean;
    is_out_of_stock: boolean;
    is_preorderable: boolean;
    is_replenishable: boolean;
    is_shaded?: boolean;
    is_sized?: boolean;
    is_under_weight_hazmat: boolean;
    maincat?: IKeyValue[];
    material_code: string;
    media: IMedia;
    out_of_stock_threshold: number;
    perlgem: IPerlgem;
    preorder_date: string;
    prices: IPrice[];
    shades: ICoreShade[];
    shade_groups: IKeyValue[];
    sizes: IKeyValue[];
    skin: ISkuSkin;
    sku_badge: string;
    sku_badge_image: string;
    sku_id: string;
    sku_promo_messaging: string;
    sku_url: string;
    texture: IValue[];
    undertone: IKeyValue[];
    unit_of_measure: string;
    unit_size: number;
    unit_size_formatted: string;
    upc_code: string;
    collection?: IKeyValue[];
    recipient?: IKeyValue[];
    occasion?: IKeyValue[];
    location?: IKeyValue[];
    vto?: IVtoData;
    finish?: IFinish[];
    tags: {
        items: ITag[];
    };
    iln_formulated_without: string;
    is_giftable?: boolean;
}

export interface IPartialParentProduct {
    productId: string;
    displayName: string;
    productUrl: string;
    defaultImage: IAsset;
}

export interface IVtoData {
    is_foundation_experience: boolean;
    is_color_experience: boolean;
}

export interface ISkuData {
    colorFamily: IKeyValue[];
    fullMaterialCode: string;
    highVelocityQuantity?: number;
    ilnListing: string;
    ilnVersionNumber: string;
    inventoryStatus: string;
    isColorExperience?: boolean;
    isDefaultSku: boolean;
    isEngravable?: boolean;
    isFoundationExperience?: boolean;
    isUpdatedInventorySource?: boolean;
    isReplenishable: boolean;
    isShaded: boolean;
    isSized: boolean;
    media: IMedia;
    perlgem: IPerlgem;
    prices: IPriceData[];
    pricePerUnit: number;
    priceObject: IPriceData | null;
    shades: IShadeData[];
    selectedSize: ISizeData;
    selectedShade: IShadeData;
    sizes: IKeyValue[];
    skin: ISkuSkin;
    skuBadge: string;
    skuId: string;
    skuUrl: string;
    undertone: IKeyValue[];
    unitSize: number;
    upcCode: string;
    texture: IValue[];
    partialParentProduct: IPartialParentProduct;
    formattedPrice: string;
    formattedOriginalPrice: string;
    isShoppable: boolean;
    tags: {
        items: ITag[];
    };
    ilnFormulatedWithout: string;
    isGiftable?: boolean;
}

export class Sku {
    @observable public colorFamily: ColorFamily[] = [];
    @observable public discounts: Discount[] = [];
    @observable public fullMaterialCode: string = '';
    @observable public ingredients: string = '';
    @observable public ilnVersionNumber: string = '';
    @observable public inventoryStatus: string = '';
    @observable public isDefaultSku: boolean;
    @observable public isDiscountable: boolean;
    @observable public isEngravable: boolean;
    @observable public isUpdatedInventorySource: boolean;
    @observable public isGiftwrap: boolean;
    @observable public isOutOfStock: boolean;
    @observable public isPreorderable: boolean;
    @observable public isReplenishable: boolean;
    @observable public isShaded: boolean;
    @observable public isSized: boolean;
    @observable public isUnderWeightHazmat: boolean;
    @observable public maincat: IKeyValue[] = [];
    @observable public media: Media;
    @observable public outOfStockThreshold: number;
    @observable public perlgem: IPerlgem;
    @observable public pricePerUnit: number;
    @observable public prices: Price[] = [];
    @observable public preorderDate: string = '';
    @observable public shades: Shade[] = [];
    @observable public sizes: Size[] = [];
    @observable public crossSellCategory: CrossSellCategory[] = [];
    @observable public skin: SkuSkin;
    @observable public skuBadge: string = '';
    @observable public skuBadgeImage: string = '';
    @observable public skuId: string = '';
    @observable public skuUrl: string = '';
    @observable public skuPromoMessaging: string = '';
    @observable public undertone: Undertone[] = [];
    @observable public unitOfMeasure: string = '';
    @observable public unitSize: number;
    @observable public unitSizeFormatted: string = '';
    @observable public upcCode: string = '';
    @observable public texture: string = '';
    @observable public parentProduct: Product;
    @observable public finishOptions: IFinishOption[] = [];
    @observable public isFavorite: boolean;
    @observable public collection?: IKeyValue[] = [];
    @observable public recipient?: IKeyValue[] = [];
    @observable public occasion?: IKeyValue[] = [];
    @observable public location?: IKeyValue[] = [];
    @observable public isFoundationExperience: boolean;
    @observable public isColorExperience: boolean;
    @observable public shadeGroups: SkuShadeGroup[];
    @observable public highVelocityQuantity?: number;
    public finish: IFinish[] = [];
    @observable public tags: { items: Tag[] };
    @observable public ilnFormulatedWithout: string = '';
    public isGiftable?: boolean;
    private currency: string;

    public constructor() {
        const { config } = diContainer.get(serviceNames.config);
        this.currency = config.currency || 'USD';
    }

    public static fromPlainObject(data: Sku): Sku {
        const newSku = new Sku();

        copyPropertiesExcludeNullsAndGetters(newSku, data);

        newSku.media = Media.fromJson(data.media);
        newSku.shades = data.shades.map(Shade.fromPlainObject);
        newSku.sizes = data.sizes.map(Size.fromJson);
        newSku.skin = SkuSkin.fromJson(data.skin);
        newSku.perlgem = data.perlgem;
        newSku.prices = data.prices.map(Price.fromPlainObject);
        newSku.finishOptions = newSku.normalizeFilter(data.finishOptions);
        newSku.finish = data.finish;

        if (data.texture && data.texture.length > 0) {
            newSku.texture = data.texture;
        }

        if (data.colorFamily && data.colorFamily.length > 0) {
            newSku.colorFamily = data.colorFamily;
        }

        if (data.undertone && data.undertone.length > 0) {
            newSku.undertone = data.undertone;
        }

        return newSku;
    }

    // eslint-disable-next-line complexity
    public static fromJson(data: ISku): Sku {
        const newSku = new Sku();

        const skuDataMapping = {
            fullMaterialCode: data.material_code,
            ingredients: data.iln_listing,
            ilnVersionNumber: data.iln_version_number,
            inventoryStatus: data.inventory_status,
            isDefaultSku: data.is_default_sku,
            isDiscountable: data.is_discountable,
            isEngravable: data.is_engravable,
            isGiftwrap: data.is_giftwrap,
            isOutOfStock: data.is_out_of_stock,
            isPreorderable: data.is_preorderable,
            isReplenishable: data.is_replenishable,
            isShaded: data.is_shaded,
            isSized: data.is_sized,
            isUnderWeightHazmat: data.is_under_weight_hazmat,
            outOfStockThreshold: data.out_of_stock_threshold,
            preorderDate: data.preorder_date,
            skuBadge: data.sku_badge,
            skuBadgeImage: data.sku_badge_image,
            skuId: data.sku_id,
            skuUrl: data.sku_url,
            unitOfMeasure: data.unit_of_measure,
            unitSize: data.unit_size,
            unitSizeFormatted: data.unit_size_formatted,
            upcCode: data.upc_code,
            skuPromoMessaging: data.sku_promo_messaging
        };
        copyPropertiesExcludeNullsAndGetters(newSku, skuDataMapping);

        newSku.discounts =
            data.discounts && data.discounts.items
                ? data.discounts.items.map(Discount.fromJson)
                : [];
        newSku.media = Media.fromJson(data.media);
        newSku.shades = data.shades ? data.shades.map(Shade.fromJson) : [];
        newSku.shadeGroups = data.shade_groups ? data.shade_groups.map(SkuShadeGroup.fromJson) : [];
        newSku.crossSellCategory = data.cross_sell
            ? data.cross_sell.categories.map(CrossSellCategory.fromJson)
            : [];
        newSku.sizes = data.sizes
            ? data.sizes
                  .filter((size) => size.value !== '' && size.value !== null)
                  .map(Size.fromJson)
            : [];
        newSku.skin = SkuSkin.fromJson(data.skin);
        newSku.perlgem = data.perlgem ? data.perlgem : { SKU_BASE_ID: 0 };
        newSku.prices = data.prices ? data.prices.map(Price.fromJson) : [];
        newSku.finishOptions = data.finish_options
            ? newSku.normalizeFilter(data.finish_options)
            : [];
        newSku.finish = data.finish ? data.finish : [];

        if (data.vto) {
            newSku.isFoundationExperience = data.vto.is_foundation_experience;
            newSku.isColorExperience = data.vto.is_color_experience;
        }

        newSku.texture = data.texture && data.texture.length > 0 ? data.texture[0].value : '';
        newSku.colorFamily =
            data.color_family && data.color_family.length > 0 ? data.color_family : [];
        newSku.undertone = data.undertone && data.undertone.length > 0 ? data.undertone : [];
        newSku.collection = data.collection && data.collection.length > 0 ? data.collection : [];
        newSku.recipient = data.recipient && data.recipient.length > 0 ? data.recipient : [];
        newSku.occasion = data.occasion && data.occasion.length > 0 ? data.occasion : [];
        newSku.location = data.location && data.location.length > 0 ? data.location : [];
        newSku.maincat = data.maincat && data.maincat.length > 0 ? data.maincat : [];
        newSku.tags = {
            items:
                data.tags && data.tags.items ? data.tags.items.map((tag) => Tag.fromJson(tag)) : []
        };
        newSku.ilnFormulatedWithout = data.iln_formulated_without || '';
        newSku.isGiftable = data.is_giftable;

        return newSku;
    }

    @computed
    public get smooshImage(): IAsset {
        const media: IMedia = this.media;
        if (!media || !media.smoosh) {
            return DEFAULT_IMAGE_ATTRIBUTE;
        }

        return media.smoosh[0];
    }

    @computed
    public get modelImage(): IAsset {
        const media: IMedia = this.media;
        if (!media || !media.model) {
            return DEFAULT_IMAGE_ATTRIBUTE;
        }

        return media.model[0];
    }

    @computed
    public get largeImages(): IAsset[] {
        const media: IMedia = this.media;
        if (!media || !media.large) {
            return [DEFAULT_IMAGE_ATTRIBUTE];
        }

        return media.large;
    }

    @computed
    public get mediumImages(): IAsset[] {
        const media: IMedia = this.media;
        if (media === undefined || !media.medium) {
            return [DEFAULT_IMAGE_ATTRIBUTE];
        }

        return media.medium;
    }

    @computed
    public get smallImages(): IAsset[] {
        const media: IMedia = this.media;
        if (!media || !media.small) {
            return [DEFAULT_IMAGE_ATTRIBUTE];
        }

        return media.small;
    }

    @computed
    public get selectedShade() {
        const { shades } = this;
        if (shades && shades.length > 0) {
            return shades.length > 1 ? Shade.formattedShades(shades) : shades[0];
        }

        return new Shade();
    }

    @computed
    public get selectedSize() {
        const { sizes } = this;
        if (sizes && sizes.length > 0) {
            return sizes.length > 1 ? Size.formattedSizes(sizes) : sizes[0];
        }

        return new Size();
    }

    @computed
    public get versionNumber() {
        return this.ilnVersionNumber;
    }

    @computed
    public get partialParentProduct() {
        return {
            productId: this.parentProduct.productId,
            displayName: this.parentProduct.displayName,
            productUrl: this.parentProduct.productUrl,
            defaultImage: this.parentProduct.defaultImage
        };
    }

    @computed
    public get priceObject() {
        const { prices, sitewideDiscount } = this;
        const skuPrices = sitewideDiscount ? sitewideDiscount.prices : prices;

        if (skuPrices && skuPrices.length > 0) {
            const priceObj: IPriceData | undefined = skuPrices.find(
                (price) => price.currency === this.currency
            );

            if (priceObj) {
                return priceObj;
            }

            return skuPrices[0];
        }

        return null;
    }

    public toJSON(): ISkuData {
        return {
            colorFamily: this.colorFamily,
            fullMaterialCode: this.fullMaterialCode,
            highVelocityQuantity: this.highVelocityQuantity,
            ilnVersionNumber: this.ilnVersionNumber,
            inventoryStatus: this.inventoryStatus,
            isDefaultSku: this.isDefaultSku,
            isReplenishable: this.isReplenishable,
            isShoppable: this.isShoppable,
            isShaded: this.isShaded,
            isSized: this.isSized,
            ilnListing: this.ingredients,
            media: this.media,
            formattedPrice: this.formattedPrice,
            formattedOriginalPrice: this.formattedOriginalPrice,
            perlgem: this.perlgem,
            prices: !!this.prices ? this.prices.map((price) => price.toJSON()) : [],
            pricePerUnit: this.pricePerUnit,
            priceObject: this.priceObject,
            shades: !!this.shades ? this.shades.map((shade) => shade.toJSON()) : [],
            sizes: !!this.sizes ? this.sizes.map((size) => size.toJSON()) : [],
            partialParentProduct: this.partialParentProduct,
            selectedSize: this.selectedSize.toJSON(),
            selectedShade: this.selectedShade.toJSON(),
            skin: this.skin.toJSON(),
            skuBadge: this.skuBadge,
            skuId: this.skuId,
            skuUrl: this.skuUrl,
            texture: [
                {
                    value: this.texture
                }
            ],
            undertone: this.undertone.map(({ key, value }) => ({
                key,
                value
            })),
            unitSize: this.unitSize,
            upcCode: this.upcCode,
            tags: this.tags,
            ilnFormulatedWithout: this.ilnFormulatedWithout
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private normalizeFilter = (filterData: any): any => {
        if (typeof filterData === 'object') {
            if (Array.isArray(filterData)) {
                return filterData.map((item) => this.normalizeFilter(item));
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return Object.keys(filterData).reduce((filterMap: any, key) => {
                filterMap[key] = this.normalizeFilter(filterData[key]);

                return filterMap;
            }, {});
        }

        return toCamelCase(filterData);
    };

    @computed
    public get hasLargeImages() {
        return this.largeImages.length > 0 && this.largeImages[0].src;
    }

    @computed
    public get hasMediumImages() {
        return this.mediumImages.length > 0 && this.mediumImages[0].src;
    }

    @computed get hasColorFamily() {
        return this.colorFamily && this.colorFamily[0] && this.colorFamily[0].value !== '';
    }

    @computed get hasUndertone() {
        return this.undertone && this.undertone[0] && this.undertone[0].value !== '';
    }

    @computed
    public get defaultSmallImage(): IAsset {
        return this.media.getDefaultMedia(ImageSizes.Small, false);
    }

    @computed
    public get defaultMediumImage(): IAsset {
        return this.media.getDefaultMedia(ImageSizes.Medium, false);
    }

    @computed
    public get defaultLargeImage(): IAsset {
        return this.media.getDefaultMedia(ImageSizes.Large, false);
    }

    @computed
    public get defaultAlternateMediumImage(): IAsset {
        return this.media.getDefaultMedia(ImageSizes.Medium, true);
    }

    @computed
    public get formattedOriginalPrice() {
        if (this.priceObject && this.originalPrice) {
            return this.priceObject.originalPriceFormatted;
        }

        return '';
    }

    @computed
    public get originalPrice() {
        if (this.priceObject) {
            return this.priceObject.originalPrice;
        }

        return null;
    }

    @computed
    public get price() {
        if (this.priceObject && this.priceObject.isDiscounted) {
            return this.priceObject.price;
        }

        return null;
    }

    @computed
    public get formattedPrice() {
        if (this.priceObject && this.price) {
            return this.priceObject.priceFormatted;
        }

        return '';
    }

    @computed
    public get formattedPricePerUnit() {
        if (this.priceObject) {
            return this.priceObject.pricePerUnitFormatted;
        }

        return '';
    }

    @computed
    public get isShoppable() {
        return SHOPPABLE_STATUSES.indexOf(InventoryStatus.valueOf(this.inventoryStatus)) !== -1;
    }

    @computed
    public get isPreorderActive() {
        return !!this.isPreorderable && this.inventoryStatus === InventoryStatus.PreOrder;
    }

    private getDiscountByType = (type: Discounts) => {
        return (
            this.discounts &&
            this.discounts.find(
                (discount) =>
                    discount.discountApplied.value === type &&
                    discount.prices.some((price) => price.currency === this.currency)
            )
        );
    };

    @computed
    public get loyaltyDiscount() {
        return this.getDiscountByType(Discounts.DISCOUNT_LOYALTY);
    }

    @computed
    public get loyaltyDiscountPrice() {
        const { prices = [] } = this.loyaltyDiscount || {};

        return prices.find((price) => price.currency === this.currency);
    }

    @computed
    public get sitewideDiscount() {
        return this.getDiscountByType(Discounts.DISCOUNT_SITEWIDE);
    }

    @computed
    public get sitewideDiscountPercent() {
        const { discountPercent } = this.sitewideDiscount || {};

        return discountPercent;
    }
}
